/**
 * AddNewEntity.js
 *
 * This component allows users to create a new entity in Firestore with fields for General Information,
 * Hours of Operation, Beds, and Meals. It includes error validation and a success message upon saving.
 *
 * Author: Meng Li
 * Created on: 2024-11-07
 */

import React, { useState } from 'react';
import UserPanelSidebar from '../components/UserPanelSidebar';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import '../../assets/css/AddNewEntity.css';

const AddNewEntity = () => {
    const [formData, setFormData] = useState({
        entity_name: '',
        sub_name: '',
        entity_type: 'Shelter',
        phone_number: '',
        email: '',
        website_211_ontario: '',
        official_website: '',
        specifications: '',
        other_resources: '',
        address: {
            address_line_1: '',
            address_line_2: '',
            city: '',
            province: '',
            country: '',
            postal_code: '',
        },
        hours_of_operation: Array(7).fill({ status: 'Open', open_time: '00:00:00', close_time: '23:59:59' }),
        beds: { bed_amount: '', beds_left: '' },
        meals: { recipe: '', meal_time: '' },
    });
    const [message, setMessage] = useState(null); // State to hold success or error message

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const [field, subField] = name.split(".");

        if (field === "beds" || field === "meals") {
            setFormData((prev) => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    [subField]: value,
                },
            }));
        } else if (field === "hours_of_operation") {
            const index = parseInt(subField, 10);
            const key = name.split(".")[2];
            setFormData((prev) => {
                const updatedHours = [...prev.hours_of_operation];
                updatedHours[index] = { ...updatedHours[index], [key]: value };
                return { ...prev, hours_of_operation: updatedHours };
            });
        } else if (name.startsWith("address.")) {
            const addressField = name.split(".")[1];
            setFormData((prev) => ({
                ...prev,
                address: {
                    ...prev.address,
                    [addressField]: value,
                },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSaveClick = async () => {
        // Error validation
        const errorMessages = [];
        const emptyFields = [];

        if (!formData.entity_name) emptyFields.push("Entity Name");
        if (!formData.phone_number) emptyFields.push("Phone Number");
        if (!formData.email) emptyFields.push("Email");
        if (!formData.address.address_line_1) emptyFields.push("Address Line 1");

        if (emptyFields.length > 0) {
            errorMessages.push(`The following fields cannot be empty: ${emptyFields.join(", ")}.`);
        }

        if (formData.address.postal_code && formData.address.postal_code.length !== 6) {
            errorMessages.push("Postal Code must be exactly six characters.");
        }

        const timeFormat = /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
        const invalidTimes = formData.hours_of_operation.filter(hour =>
            hour.status === 'Open' &&
            (!timeFormat.test(hour.open_time) || !timeFormat.test(hour.close_time))
        );

        if (invalidTimes.length > 0) {
            errorMessages.push("One or more times are not in the correct format (HH:MM:SS). Please check and try again.");
        }

        if (errorMessages.length > 0) {
            setMessage(errorMessages.join(" "));
            return;
        }

        try {
            await addDoc(collection(db, 'entities'), formData);
            setMessage("Your entity has been saved successfully!");
        } catch (error) {
            console.error("Error saving new entity:", error);
            setMessage("Error saving entity. Please try again.");
        }
    };

    return (
        <div className="add-new-entity-page">
            <UserPanelSidebar />
            <div className="add-new-entity-container">
                {/* General Information Section */}
                <div className="section">
                    <h2 className="section-title">General Information</h2>
                    <div className="input-wrapper">
                        <input name="entity_name" value={formData.entity_name} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Entity Name</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="sub_name" value={formData.sub_name} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Subtitle</label>
                    </div>
                    <div className="input-wrapper">
                        <select name="entity_type" value={formData.entity_type} onChange={handleInputChange} className="input-field">
                            <option value="Shelter">Shelter</option>
                            <option value="HomelessRespite">HomelessRespite</option>
                            <option value="Free/LowCostMeals">Free/LowCostMeals</option>
                            <option value="StreetOutreach">StreetOutreach</option>
                        </select>
                        <label className="input-label">Type</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="phone_number" value={formData.phone_number} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Phone Number</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="email" value={formData.email} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Email</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="website_211_ontario" value={formData.website_211_ontario} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">211 Ontario Website</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="official_website" value={formData.official_website} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Official Website</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="specifications" value={formData.specifications} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Specifications</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="other_resources" value={formData.other_resources} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Other Resources</label>
                    </div>
                    {/* Address Section */}
                    <div className="input-wrapper">
                        <input name="address.address_line_1" value={formData.address.address_line_1} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Address Line 1</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="address.address_line_2" value={formData.address.address_line_2} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Address Line 2</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="address.city" value={formData.address.city} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">City</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="address.province" value={formData.address.province} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Province</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="address.country" value={formData.address.country} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Country</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="address.postal_code" value={formData.address.postal_code} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Postal Code</label>
                    </div>
                </div>

                {/* Hours of Operation Section */}
                <div className="section">
                    <h2 className="section-title">Hours of Operation</h2>
                    {formData.hours_of_operation.map((hour, index) => (
                        <div key={index} className="hours-row">
                            <span className="day-label">{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][index]}</span>
                            <select name={`hours_of_operation.${index}.status`} value={hour.status} onChange={handleInputChange} className="status-select">
                                <option value="Open">Open</option>
                                <option value="Closed">Closed</option>
                            </select>
                            <input type="text" name={`hours_of_operation.${index}.open_time`} value={hour.open_time} onChange={handleInputChange} className="time-input" placeholder="HH:MM:SS" />
                            <input type="text" name={`hours_of_operation.${index}.close_time`} value={hour.close_time} onChange={handleInputChange} className="time-input" placeholder="HH:MM:SS" />
                        </div>
                    ))}
                </div>

                {/* Beds Section */}
                <div className="section">
                    <h2 className="section-title">Beds</h2>
                    <div className="input-wrapper">
                        <input name="beds.bed_amount" value={formData.beds.bed_amount} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Bed Amount</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="beds.beds_left" value={formData.beds.beds_left} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Beds Left</label>
                    </div>
                </div>

                {/* Meals Section */}
                <div className="section">
                    <h2 className="section-title">Meals</h2>
                    <div className="input-wrapper">
                        <input name="meals.recipe" value={formData.meals.recipe} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Today's Recipe</label>
                    </div>
                    <div className="input-wrapper">
                        <input name="meals.meal_time" value={formData.meals.meal_time} onChange={handleInputChange} className="input-field" />
                        <label className="input-label">Meal Times</label>
                    </div>
                </div>

                {/* Save Button */}
                <div className="button-container">
                    <button className="save-button" onClick={handleSaveClick}>Save</button>
                </div>

                {/* Success/Error Message Modal */}
                {message && (
                    <div className="modal" onClick={() => setMessage(null)}>
                        <div className="modal-content">
                            <p>{message}</p>
                            <button onClick={() => setMessage(null)} className="close-btn">Close</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddNewEntity;
